import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, LOCALE_ID, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withPreloading, PreloadAllModules, withViewTransitions, Router, TitleStrategy } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { ConfirmationService, MessageService } from 'primeng/api';
import { httpAuthInterceptor } from './core/http/http-auth.interceptor';
import { httpClientDataInterceptor } from './core/http/http-client-data.interceptor';
import { httpErrorInterceptor } from './core/http/http-error.interceptor';
import { demoMockInterceptor } from './core/demo/demo-mock.interceptor';
import { httpLocaleInterceptor } from './core/http/http-locale.interceptor';
import { JestyTitleStrategy } from './core/config/jesty-title.strategy';
import { appLocaleToLang, FALLBACK_APP_LANG, resolveAppLocale, SUPPORTED_APP_LANGS } from './core/config/app-locale';
import { primeNgTranslation } from './core/i18n/primeng-i18n';
import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideLottieOptions } from 'ngx-lottie';
import { environment } from '../environments/environment';

const JestyLinePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eef4ff',
      100: '#dbe8ff',
      200: '#bdd2ff',
      300: '#8eb0ff',
      400: '#5c8bff',
      500: '#3773fe',
      600: '#2a62e8',
      700: '#1f52d1',
      800: '#1a47b2',
      900: '#163c92',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    providePrimeNG({
      theme: {
        preset: JestyLinePreset,
        options: {
          darkModeSelector: '.theme-dark',
        },
      },
      ripple: false,
      translation: primeNgTranslation(appLocaleToLang(resolveAppLocale())),
    }),
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_APP_LANGS],
        // Aktif dil = çözümlenen locale; İngilizce temel/fallback dildir.
        defaultLang: appLocaleToLang(resolveAppLocale()),
        fallbackLang: FALLBACK_APP_LANG,
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: { useFallbackTranslation: true },
        // Scope adlarını olduğu gibi koru: `incoming-invoices`, `bank-integrations` gibi tireli
        // scope'lar aksi halde camelCase'e (`incomingInvoices`) çevrilir ve şablondaki
        // `incoming-invoices.*` anahtarları çözülemeyip ham anahtar olarak görünür.
        scopes: { keepCasing: true },
      },
      loader: TranslocoHttpLoader,
    }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withPreloading(PreloadAllModules), withViewTransitions()),
    { provide: TitleStrategy, useClass: JestyTitleStrategy },
    provideHttpClient(
      withInterceptors([httpLocaleInterceptor, httpClientDataInterceptor, httpAuthInterceptor, httpErrorInterceptor]),
    ),
    { provide: LOCALE_ID, useFactory: resolveAppLocale },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true },
    MessageService,
    ConfirmationService,
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
  ],
};
